import React, { useEffect, useState } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Hero from "src/components/Hero/Hero";
import DrawCard from "src/components/PromoDrawsCard/DrawCard";
import TitleSection from "src/components/TitleSection/TitleSection";
import {
  getPromoDrawsScheduleDates,
  shouldPublish,
} from "src/utils/publishUtils";
import {
  getPromotionData,
  sortPromotionsByDisplayOrder
} from "src/utils/promoUtils";
import {
  processMultiDrawPromotions,
  buildPromoData
} from "src/utils/multiDrawUtils";
import styled from "styled-components";
import { DateTime } from "luxon";
import { darkCardStyle } from "src/GlobalStyle";
import { useAccount } from "src/context/account/account.provider";
import Markdown from "src/components/Markdown";
import promoDrawsScheduleConfig from "src/config/promoDrawsScheduleConfig";

const now = DateTime.now();

const PromoDrawsPage = () => {
  const { t, i18n } = useTranslation();
  const { electronicEntriesSummary, electronicEntriesSummaryFn } = useAccount();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPromoDraws = async () => {
      try {
        // Get the published promo draws from the config (both poker and EGD)
        const publishedPromoDraws = Object.entries(promoDrawsScheduleConfig)
          .filter(([_, config]) => shouldPublish(config))
          .map(([promotionId, config]) => ({
            promotionId,
            key: config.key,
            drawType: config.drawType,
            isMultiDraw: config.isMultiDraw,
            subDraws: config.subDraws
          }));

        // Extract all promotion IDs with their draw types
        const allPromotionIds = publishedPromoDraws.flatMap(promo => {
          const ids = promo.isMultiDraw && promo.subDraws
            ? promo.subDraws.map(subDraw => subDraw.promotionId)
            : [promo.promotionId];

          return ids.map(id => ({ id, drawType: promo.drawType }));
        });

        // Separate by draw type and uppercase all IDs
        const pokerPromotionIds = allPromotionIds
          .filter(item => item.drawType === 'poker')
          .map(item => item.id.toUpperCase());

        const egdPromotionIds = allPromotionIds
          .filter(item => item.drawType === 'egd')
          .map(item => item.id.toUpperCase());

        // Call API with all three parameter types for smooth transition
        if (pokerPromotionIds.length > 0 || egdPromotionIds.length > 0) {
          await electronicEntriesSummaryFn({
            promotionIds: pokerPromotionIds, // Current format (backward compatibility)
            poker_ids: pokerPromotionIds,     // New format for poker draws
            egd_ids: egdPromotionIds          // New format for EGD draws
          });
        } else {
          console.log("No published promo draws found");
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };
    fetchPromoDraws();
  }, [i18n.language]);

  useEffect(() => {
    if (
      Array.isArray(electronicEntriesSummary) ||
      electronicEntriesSummary?.type === "error"
    ) {
      setLoading(false);
    }
  }, [electronicEntriesSummary]);

  return (
    <DrawsSection>
      <Hero
        mainHeroText={t("promoDrawsPage.mainHeroText")}
        description={t("promoDrawsPage.heroDescription")}
        bgImage={"about/about-us-hero-bg_v2.jpg"}
        darkenBg={true}
        className="mb-5"
      />
      <container-div>
        {/* <TitleSection
          dark={false}
          as={"h2"}
          title={t("promoDrawsPage.contentTitle")}
          text={t("promoDrawsPage.contentDescription")}
        /> */}
        {loading ? (
          <EmptyCard className="p-3 mx-auto">
            <p className="pb-3">{t("promoDrawsPage.loadingPromos")}</p>
            <Spinner animation="border" variant="light" />
          </EmptyCard>
        ) : electronicEntriesSummary?.type === "error" ? (
          <Row xs={1} sm={1} md={2} lg={3}>
            <CardCol>
              <DrawCard
                error={electronicEntriesSummary}
              />
            </CardCol>
          </Row>
        ) : Array.isArray(electronicEntriesSummary) && electronicEntriesSummary.length > 0 ? (
          <Row xs={1} sm={1} md={2} lg={3}>
            {(() => {
              // Process promotions to handle multi-draw logic
              const processedPromotions = processMultiDrawPromotions(
                electronicEntriesSummary,
                promoDrawsScheduleConfig,
                shouldPublish
              );

              return sortPromotionsByDisplayOrder(processedPromotions.map(p => p.draw))
                .map((draw) => {
                  const processed = processedPromotions.find(p => p.draw.promotion_id === draw.promotion_id);
                  if (!processed) return null;

                  const { config, subDrawData } = processed;
                  const promoData = buildPromoData(config, t);

                  return (
                    <CardCol key={draw?.promotion_id}>
                      <DrawCard
                        draw={draw}
                        promoData={promoData}
                        subDrawData={subDrawData}
                      />
                    </CardCol>
                  );
                })
                .filter(Boolean);
            })()}
          </Row>
        ) : (
          <EmptyCard className="p-3 mx-auto">
            <h3 className="pb-3">{t("promoDrawsPage.noDraws")}</h3>
            <Markdown>{t("promoDrawsPage.noDrawsDescription")}</Markdown>
          </EmptyCard>
        )}
      </container-div>
    </DrawsSection>
  );
};

const DrawsSection = styled.section`
  background: black;
  color: white;
  text-align: center;
  padding: 3rem 0;
  .main-hero-text h1 {
    @media screen and (max-width: 768px) {
      font-size: 2rem;
    }
  }
`;

const CardCol = styled(Col)`
  margin: 0 auto;
`;

const EmptyCard = styled.div`
  ${darkCardStyle}
  max-width: 500px;
`;

export default PromoDrawsPage;
